/**
 * GymLog: pure domain logic (no DB, no UI) — 100% testable.
 *
 *   - Estimated 1RM (Epley formula)
 *   - PRs per exercise (max weight, best 1RM, best session volume)
 *   - Detection of new PRs upon completing a set
 *   - Preload of last set (weight×reps×RPE)
 *   - Weekly volume by muscle group
 */

import type { Exercise } from "@gymlog/powersync";
import { toNumber } from "@fleet/utils";
import { DEFAULT_BAR_WEIGHT_KG, STANDARD_PLATES_KG } from "./constants";

const EPLEY_DIVISOR = 30;

/**
 * Estimated 1RM using Epley formula: `w · (1 + r/30)`.
 * For 1 rep returns the weight itself; for invalid input returns 0.
 */
export function epleyOneRepMax(weightKg: number, reps: number): number {
  if (!Number.isFinite(weightKg) || !Number.isFinite(reps) || weightKg <= 0 || reps <= 0) {
    return 0;
  }
  if (reps === 1) {
    return weightKg;
  }
  return weightKg * (1 + reps / EPLEY_DIVISOR);
}

/** Volume of a set: weight × reps (kg). */
export function setVolume(weightKg: number, reps: number): number {
  if (!Number.isFinite(weightKg) || !Number.isFinite(reps) || weightKg < 0 || reps <= 0) {
    return 0;
  }
  return weightKg * reps;
}

/** Subset of `workout_set` columns required by PR logic. */
export interface SetLike {
  id?: string | null;
  session_id?: string | null;
  exercise_id?: string | null;
  weight_kg: number | null;
  reps: number | null;
  rpe?: number | null;
  is_warmup?: number | null;
  completed_at?: string | null;
}

export interface PrRecord {
  value: number;
  /** ISO date of the set/session that set the record. */
  date: string | null;
}

export interface ExercisePRs {
  /** Maximum weight lifted (kg) in a working set. */
  maxWeight: PrRecord | null;
  /** Best estimated 1RM (Epley) of a working set. */
  best1Rm: PrRecord | null;
  /** Greatest volume (kg) accumulated in a single session. */
  maxSessionVolume: PrRecord | null;
}

function isWorkingSet(s: SetLike): boolean {
  return toNumber(s.is_warmup) !== 1 && toNumber(s.weight_kg) > 0 && toNumber(s.reps) > 0;
}

/**
 * Computes active PRs for an exercise from its set history.
 * Warm-ups are excluded. Returns null for each metric if no data.
 */
export function computeExercisePRs(sets: readonly SetLike[]): ExercisePRs {
  let maxWeight: PrRecord | null = null;
  let best1Rm: PrRecord | null = null;
  const volumeBySession = new Map<string, { volume: number; date: string | null }>();

  for (const s of sets) {
    if (!isWorkingSet(s)) {
      continue;
    }
    const weight = toNumber(s.weight_kg);
    const reps = toNumber(s.reps);
    const date = s.completed_at ?? null;

    if (!maxWeight || weight > maxWeight.value) {
      maxWeight = { value: weight, date };
    }
    const oneRm = epleyOneRepMax(weight, reps);
    if (!best1Rm || oneRm > best1Rm.value) {
      best1Rm = { value: oneRm, date };
    }

    const sessionId = s.session_id ?? "_";
    const entry = volumeBySession.get(sessionId) ?? { volume: 0, date };
    entry.volume += setVolume(weight, reps);
    // Session date = date of the last set logged within it.
    if (date && (!entry.date || date > entry.date)) {
      entry.date = date;
    }
    volumeBySession.set(sessionId, entry);
  }

  let maxSessionVolume: PrRecord | null = null;
  for (const { volume, date } of volumeBySession.values()) {
    if (!maxSessionVolume || volume > maxSessionVolume.value) {
      maxSessionVolume = { value: volume, date };
    }
  }

  return { maxWeight, best1Rm, maxSessionVolume };
}

export interface NewPrFlags {
  isWeightPr: boolean;
  is1RmPr: boolean;
}

/**
 * Does the newly completed set beat any record relative to PREVIOUS history?
 *
 * - Previous history must NOT include the new set.
 * - The first working set of an exercise counts as a PR (inaugural record).
 * - Warm-ups never score.
 */
export function detectNewPRs(previousSets: readonly SetLike[], newSet: SetLike): NewPrFlags {
  if (!isWorkingSet(newSet)) {
    return { isWeightPr: false, is1RmPr: false };
  }
  const prs = computeExercisePRs(previousSets);
  const weight = toNumber(newSet.weight_kg);
  const oneRm = epleyOneRepMax(weight, toNumber(newSet.reps));

  return {
    isWeightPr: !prs.maxWeight || weight > prs.maxWeight.value,
    is1RmPr: !prs.best1Rm || oneRm > prs.best1Rm.value,
  };
}

export interface PreloadedSet {
  weightKg: number;
  reps: number;
  rpe: number | null;
}

/**
 * Preload for next set of an exercise: last WORKING set
 * logged (across any session); if only warm-ups exist, the last warm-up.
 * `sets` may arrive in any order — sorted by completed_at.
 */
export function pickPreloadSet(sets: readonly SetLike[]): PreloadedSet | null {
  if (sets.length === 0) {
    return null;
  }
  const sorted = sets.toSorted((a, b) =>
    (a.completed_at ?? "").localeCompare(b.completed_at ?? ""),
  );
  const lastWorking = sorted.filter((s) => toNumber(s.is_warmup) !== 1).at(-1);
  const chosen = lastWorking ?? sorted.at(-1);
  if (!chosen) {
    return null;
  }
  return {
    weightKg: toNumber(chosen.weight_kg),
    reps: toNumber(chosen.reps),
    rpe: toNumberOrNull(chosen.rpe),
  };
}

function toNumberOrNull(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

/** Total volume (kg) of a collection of sets (warm-ups excluded). */
export function totalVolume(sets: readonly SetLike[]): number {
  let total = 0;
  for (const s of sets) {
    if (isWorkingSet(s)) {
      total += setVolume(toNumber(s.weight_kg), toNumber(s.reps));
    }
  }
  return total;
}

export interface MuscleGroupVolume {
  muscleGroup: string;
  volumeKg: number;
  sets: number;
}

/** Minimal row for weekly aggregation: set + exercise muscle group. */
export interface SetWithMuscleGroup extends SetLike {
  muscle_group: string | null;
}

/**
 * Aggregates volume and working set count by muscle group.
 * Sorted descending by volume.
 */
export function volumeByMuscleGroup(rows: readonly SetWithMuscleGroup[]): MuscleGroupVolume[] {
  const acc = new Map<string, MuscleGroupVolume>();
  for (const row of rows) {
    if (!isWorkingSet(row)) {
      continue;
    }
    const group = row.muscle_group ?? "other";
    const entry = acc.get(group) ?? {
      muscleGroup: group,
      volumeKg: 0,
      sets: 0,
    };
    entry.volumeKg += setVolume(toNumber(row.weight_kg), toNumber(row.reps));
    entry.sets += 1;
    acc.set(group, entry);
  }
  return [...acc.values()].toSorted((a, b) => b.volumeKg - a.volumeKg);
}

const DAYS_PER_WEEK = 7;
const SUNDAY = 0;

/**
 * Start of the week (Monday 00:00 local time) for a given date.
 */
export function startOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const diff = day === SUNDAY ? DAYS_PER_WEEK - 1 : day - 1;
  result.setDate(result.getDate() - diff);
  result.setHours(0, 0, 0, 0);
  return result;
}

/** Exercise name by locale ('es*' → name_es; other → name_en). */
export function exerciseDisplayName(
  ex: Pick<Exercise, "name_en" | "name_es">,
  locale: string,
): string {
  const name = locale.startsWith("es") ? ex.name_es : ex.name_en;
  return name ?? ex.name_en ?? ex.name_es ?? "";
}

/** Session duration in whole minutes (0 if either bound is missing). */
export function sessionDurationMinutes(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
): number {
  if (!startedAt || !finishedAt) {
    return 0;
  }
  const start = Date.parse(startedAt);
  const end = Date.parse(finishedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return 0;
  }
  const MS_PER_MINUTE = 60_000;
  return Math.round((end - start) / MS_PER_MINUTE);
}

/** Next set number within a session for an exercise. */
export function nextSetNumber(sessionSets: readonly SetLike[], exerciseId: string): number {
  let count = 0;
  for (const s of sessionSets) {
    if (s.exercise_id === exerciseId) {
      count += 1;
    }
  }
  return count + 1;
}

// ───────────────────────── Plate calculator ─────────────────────────────
//
// Given target weight, bar weight, and available plates, which plates
// load ON EACH SIDE? Pure symmetric logic (load is identical on both
// sides). Integer centi-kg arithmetic avoids floating point drift
// with 1.25/2.5 kg plates.

/** Hundredths of kg per kg (integer arithmetic prevents FP drift). */
const CENTI_KG = 100;

/** Individual plate group: denomination × count (per side). */
export interface PlateGroup {
  plateKg: number;
  count: number;
}

export interface PlateLoadout {
  /** Plates to load on ONE side of the bar, descending (kg). */
  perSide: number[];
  /** Actual loaded weight: bar + 2·Σ(perSide) (kg). */
  achievableKg: number;
  /** Requested target weight (kg). */
  targetKg: number;
  /** Bar weight used (kg). */
  barKg: number;
  /** target − achievable, ≥ 0: kg that could not be matched with plates. */
  remainderKg: number;
  /** Target is less than empty bar (nothing to load). */
  belowBar: boolean;
}

/**
 * Computes per-side load for target weight using a greedy algorithm over
 * plates sorted descending (assuming sufficient count of each).
 *
 * - Non-finite / non-positive input → target 0 (empty loadout).
 * - `target < bar` → `belowBar` and no plates (cannot load).
 * - `remainderKg > 0` when available plates cannot match exact target.
 */
export function computePlateLoadout(
  targetKg: number,
  barKg: number = DEFAULT_BAR_WEIGHT_KG,
  availablePlatesKg: readonly number[] = STANDARD_PLATES_KG,
): PlateLoadout {
  const safeBar = Number.isFinite(barKg) && barKg > 0 ? barKg : DEFAULT_BAR_WEIGHT_KG;
  const target = Number.isFinite(targetKg) && targetKg > 0 ? targetKg : 0;

  if (target === 0 || target < safeBar) {
    return {
      perSide: [],
      achievableKg: safeBar,
      targetKg: target,
      barKg: safeBar,
      remainderKg: 0,
      belowBar: target > 0 && target < safeBar,
    };
  }

  let perSideRemainingCenti = Math.round(((target - safeBar) / 2) * CENTI_KG);
  const plates = availablePlatesKg
    .filter((p) => Number.isFinite(p) && p > 0)
    .toSorted((a, b) => b - a);

  const perSide: number[] = [];
  for (const plate of plates) {
    const plateCenti = Math.round(plate * CENTI_KG);
    while (perSideRemainingCenti >= plateCenti) {
      perSide.push(plate);
      perSideRemainingCenti -= plateCenti;
    }
  }

  const loadedPerSideCenti = perSide.reduce((sum, p) => sum + Math.round(p * CENTI_KG), 0);
  const achievableKg = safeBar + (loadedPerSideCenti * 2) / CENTI_KG;
  const remainderKg = Math.round((target - achievableKg) * CENTI_KG) / CENTI_KG;

  return {
    perSide,
    achievableKg,
    targetKg: target,
    barKg: safeBar,
    remainderKg,
    belowBar: false,
  };
}

/**
 * Groups a list of plates (descending) into ranges `{plateKg, count}`.
 * E.g. `[20, 20, 5]` → `[{20, 2}, {5, 1}]`.
 */
export function groupPlates(perSide: readonly number[]): PlateGroup[] {
  const groups: PlateGroup[] = [];
  for (const plate of perSide) {
    const last = groups.at(-1);
    if (last && last.plateKg === plate) {
      last.count += 1;
    } else {
      groups.push({ plateKg: plate, count: 1 });
    }
  }
  return groups;
}
